import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Cat, Moon, Sun } from 'lucide-react'

/**
 * Animated floating particles background effect
 */
const FloatingParticles = () => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-purple-400/20 rounded-full"
          animate={{
            x: [0, 100, 0],
            y: [0, -100, 0],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: Math.random() * 10 + 15,
            repeat: Infinity,
            delay: Math.random() * 5,
            ease: "easeInOut",
          }}
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
        />
      ))}
    </div>
  )
}

/**
 * Dark mode theme toggle icon with cat emoji
 */
const MoonCat = () => (
  <div className="relative">
    <Moon className="h-5 w-5 text-amber-400" />
    <div className="absolute -top-1 -right-1 text-xs">🐱</div>
  </div>
)

/**
 * Light mode theme toggle icon with cat emoji
 */
const SunCat = () => (
  <div className="relative">
    <Sun className="h-5 w-5 text-purple-600" />
    <div className="absolute -top-1 -right-1 text-xs">😸</div>
  </div>
)

function App() {
  const [theme, setTheme] = useState<'dark' | 'light'>('dark')
  const [progress, setProgress] = useState(30)
  const [mounted, setMounted] = useState(false)

  // Initialize theme and background on mount
  useEffect(() => {
    setMounted(true)
    document.documentElement.setAttribute('data-theme', theme)
    // Prevent horizontal scroll
    document.body.style.overflowX = 'hidden'
    if (theme === 'light') {
      document.body.style.background = 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    } else {
      document.body.style.background = 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
    }
  }, [theme])

  // Animated progress bar logic
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => prev < 100 ? prev + 10 : prev)
    }, 2000)
    return () => clearInterval(timer)
  }, [])

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark')
  }

  // Prevent hydration mismatch
  if (!mounted) return null

  return (
    <div className={`min-h-screen overflow-x-hidden transition-all duration-700 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 text-slate-100' 
        : 'bg-gradient-to-br from-slate-50 via-white to-slate-100 text-slate-900'
    }`}>
      <FloatingParticles />
      
      {/* Animated background blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className={`absolute -top-40 -left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-60 ${
            theme === 'dark' ? 'bg-purple-600' : 'bg-purple-400'
          }`}
          animate={{ x: [0, 50, 0], y: [0, 30, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute -bottom-40 -right-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-60 ${
            theme === 'dark' ? 'bg-blue-600' : 'bg-blue-400'
          }`}
          animate={{ x: [0, -30, 0], y: [0, -50, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Top navigation bar - Only theme toggle button (no social links) */}
      <div className="fixed top-4 sm:top-6 left-4 sm:left-6 right-4 sm:right-6 z-50">
        <div className="flex justify-end">
          <motion.button
            onClick={toggleTheme}
            className={`rounded-full p-3 backdrop-blur-md transition-all border ${
              theme === 'dark' 
                ? 'bg-white/10 hover:bg-white/20 border-white/20' 
                : 'bg-black/10 hover:bg-black/20 border-black/20'
            }`}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0, rotate: -180 }}
            animate={{ opacity: 1, rotate: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <AnimatePresence mode="wait">
              {theme === 'dark' ? (
                <motion.div
                  key="moon"
                  initial={{ opacity: 0, rotate: -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: 90 }}
                  transition={{ duration: 0.3 }}
                >
                  <MoonCat />
                </motion.div>
              ) : (
                <motion.div
                  key="sun"
                  initial={{ opacity: 0, rotate: -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: 90 }}
                  transition={{ duration: 0.3 }}
                >
                  <SunCat />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </div>

      {/* Main content area */}
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-40 sm:pt-36 lg:pt-32 overflow-x-hidden">
        <div className="w-full max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            
            {/* Left column: Animated cat logo */}
            <div className="lg:col-span-5 flex justify-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="relative"
              >
                <div className="relative w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48">
                  {/* Pulsing background ring */}
                  <motion.div
                    className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-600 via-blue-600 to-emerald-600 opacity-20"
                    animate={{ 
                      scale: [1, 1.4, 1],
                      opacity: [0.2, 0.1, 0.2],
                      rotate: [0, 180, 360]
                    }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  />
                  
                  {/* Main cat icon with gradient background */}
                  <motion.div
                    className={`relative flex w-full h-full items-center justify-center rounded-full shadow-2xl ${
                      theme === 'dark'
                        ? 'bg-gradient-to-r from-purple-600 via-blue-600 to-emerald-600'
                        : 'bg-gradient-to-r from-purple-500 via-blue-500 to-emerald-500'
                    }`}
                    whileHover={{ 
                      scale: 1.1,
                      boxShadow: theme === 'dark' 
                        ? "0 0 50px rgba(147, 51, 234, 0.6)" 
                        : "0 0 50px rgba(147, 51, 234, 0.4)"
                    }}
                    animate={{ y: [0, -8, 0] }}
                    transition={{ 
                      y: { duration: 3, repeat: Infinity, ease: "easeInOut" },
                      hover: { duration: 0.3 }
                    }}
                  >
                    <Cat className="h-12 w-12 sm:h-16 sm:w-16 lg:h-20 lg:w-20 text-white drop-shadow-lg" />
                    
                    {/* Rotating decorative elements */}
                    <motion.div
                      className="absolute -top-2 -right-2 text-lg sm:text-xl lg:text-2xl"
                      animate={{ rotate: [0, 360] }}
                      transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    >
                      ✨
                    </motion.div>
                    <motion.div
                      className="absolute -bottom-2 -left-2 text-base sm:text-lg lg:text-xl"
                      animate={{ rotate: [360, 0] }}
                      transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                    >
                      🌟
                    </motion.div>
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Right column: Main content */}
            <div className="lg:col-span-7 text-center lg:text-left space-y-6 lg:space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <h1 className={`text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight tracking-tight mb-4 ${
                  theme === 'dark' ? 'text-white' : 'text-slate-900'
                }`}>
                  <motion.span 
                    className="bg-gradient-to-r from-purple-400 via-blue-400 to-emerald-400 bg-clip-text text-transparent font-extrabold block"
                    animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    Excuse the Chaos
                  </motion.span>
                </h1>
                
                <h2 className={`text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold ${
                  theme === 'dark' ? 'text-slate-200' : 'text-slate-800'
                }`}>
                  I'm Making Things Purrfect! 🐾
                </h2>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className={`space-y-4 text-base sm:text-lg lg:text-xl ${
                  theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                }`}
              >
                <p>
                  I'm currently building a brand new site, and things are a little <em className="text-purple-400 font-semibold">wild</em> around here!
                </p>
                <p>
                  But don't worry - I'm paws-itively working around the clock to get everything ready for you.
                </p>
                <p>
                  Check back soon - it's going to be <strong className="text-emerald-400 font-bold">pawsome</strong>!
                </p>
              </motion.div>
            </div>
          </div>

          {/* Progress bar section */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-12 lg:mt-16"
          >
            <div className="mx-auto max-w-full px-4 sm:px-6 lg:px-8">
              <div className={`mb-4 flex justify-between text-sm sm:text-base font-semibold ${
                theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
              }`}>
                <span>Building Progress</span>
                <motion.span
                  key={progress}
                  initial={{ scale: 1.2, color: '#10B981' }}
                  animate={{ scale: 1, color: 'inherit' }}
                  transition={{ duration: 0.3 }}
                >
                  {progress}%
                </motion.span>
              </div>
              {/* Progress bar with animated fill */}
              <div className={`h-3 sm:h-4 w-full overflow-hidden rounded-full shadow-inner ${
                theme === 'dark' ? 'bg-slate-700' : 'bg-slate-300'
              }`}>
                <motion.div
                  className="h-full bg-gradient-to-r from-purple-500 via-blue-500 to-emerald-500 shadow-lg"
                  initial={{ width: '30%' }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                >
                  {/* Shimmer effect on progress bar */}
                  <motion.div
                    className="h-full w-full bg-gradient-to-r from-white/20 to-transparent"
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  />
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer - Clean and minimal */}
      <motion.footer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        className="relative p-4 sm:p-6 text-center pb-20 sm:pb-8"
      >
        <div className={`text-xs sm:text-sm font-medium ${
          theme === 'dark' ? 'text-slate-400' : 'text-slate-600'
        }`}>
          <div className="flex flex-col items-center justify-center gap-1">
            <div className="flex items-center gap-1">
              <motion.span
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="flex-shrink-0"
              >
                🐾
              </motion.span>
              <span>Building something amazing</span>
              <motion.span
                animate={{ rotate: [0, -360] }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                className="flex-shrink-0"
              >
                ✨
              </motion.span>
            </div>
          </div>
          <div className="text-xs opacity-75 px-4 mt-1">
            Coming soon · Stay tuned
          </div>
        </div>
      </motion.footer>
    </div>
  )
}

export default App
